/**
 * Sprint 12+B (02.07.2026) — Pionier-Confirm-Seite.
 * Sprint 12+H (03.07.2026) — Dedizierter Pionier-Confirm über zweite Route.
 * Sprint 12+L4.2 (14.07.2026) — Konsolidiert: EINE Verify-Komponente für
 *   beide Routen. Wählt den Backend-Endpoint anhand des aktuellen Pfads.
 *   Alter Alias-Component `WartelistePionierConfirm.jsx` ist gelöscht.
 *
 * Wird über Autoresponder-Link `?token=xxx` aufgerufen. States:
 *   Loading  — Token wird gegen Backend verifiziert
 *   Success  — 200 OK: 2s Auto-Redirect zu /warteliste/danke
 *   Error    — 4xx/5xx: differenzierte Meldung + Support-Kontakt
 *
 * Route → Endpoint-Mapping:
 *   /warteliste/confirm         → POST /api/warteliste/confirm-token
 *   /warteliste/pionier-confirm → POST /api/warteliste/pionier-opt-in-confirm
 *
 * Beide Backend-Endpoints haben ab Sprint 12+L4.2 identische Tag-Semantik
 * (`add_tag(pionier_flex4success)`), unterscheiden sich nur im Cross-Flow-
 * Guard-Verhalten und den Response-Feldern.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const STATE_LOADING = "loading";
const STATE_SUCCESS = "success";
const STATE_ERROR = "error";

const REDIRECT_MS = 2500;

// Route → Endpoint + Response-Adapter
function pickEndpointForPath(pathname) {
  if (pathname && pathname.startsWith("/warteliste/pionier-confirm")) {
    return {
      url: `${API}/api/warteliste/pionier-opt-in-confirm`,
      // Sprint 12+H Response: {status, user_id, redirect_url, already_confirmed, logged_in}
      parseSuccess: (body) => ({
        alreadyConfirmed: Boolean(body.already_confirmed),
        loggedIn: Boolean(body.logged_in),
      }),
    };
  }
  return {
    url: `${API}/api/warteliste/confirm-token`,
    // Sprint 12+B Response: {success, is_pionier, already_confirmed, logged_in}
    parseSuccess: (body) => ({
      alreadyConfirmed: Boolean(body.already_confirmed),
      loggedIn: Boolean(body.logged_in),
    }),
  };
}

export default function PionierConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh } = useAuth() || {};
  const [state, setState] = useState(STATE_LOADING);
  const [errorCode, setErrorCode] = useState(null);
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false);

  useEffect(() => {
    const token = (searchParams.get("token") || "").trim();
    if (!token) {
      setErrorCode("token_missing");
      setState(STATE_ERROR);
      return;
    }

    const { url, parseSuccess } = pickEndpointForPath(location.pathname);

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(url, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        if (res.ok) {
          const body = await res.json();
          const parsed = parseSuccess(body);
          setAlreadyConfirmed(parsed.alreadyConfirmed);
          if (parsed.loggedIn && typeof refresh === "function") {
            try {
              await refresh();
            } catch (_) {
              /* non-fatal */
            }
          }
          setState(STATE_SUCCESS);
          setTimeout(() => {
            if (!cancelled) navigate("/warteliste/danke", { replace: true });
          }, REDIRECT_MS);
        } else {
          let code = "unknown";
          try {
            const err = await res.json();
            code = (err.detail || "").split(":")[0] || `http_${res.status}`;
          } catch {
            code = `http_${res.status}`;
          }
          setErrorCode(code);
          setState(STATE_ERROR);
        }
      } catch (e) {
        if (cancelled) return;
        setErrorCode("network_error");
        setState(STATE_ERROR);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, location.pathname]);

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center px-4"
      data-testid="pionier-confirm-page"
    >
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-10">
        {state === STATE_LOADING && <LoadingView />}
        {state === STATE_SUCCESS && (
          <SuccessView alreadyConfirmed={alreadyConfirmed} navigate={navigate} />
        )}
        {state === STATE_ERROR && <ErrorView errorCode={errorCode} />}
      </div>
    </div>
  );
}

function LoadingView() {
  return (
    <div
      className="flex flex-col items-center text-center py-6"
      data-testid="pionier-confirm-loading"
    >
      <Loader2 className="w-10 h-10 text-[#4563BB] animate-spin mb-4" />
      <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
        Bestätige deine Anmeldung...
      </h1>
      <p className="mt-2 text-sm text-slate-500">
        Einen Moment, wir prüfen deinen Link.
      </p>
      <div className="mt-8 w-full space-y-3">
        <div className="h-3 bg-slate-100 rounded animate-pulse" />
        <div className="h-3 bg-slate-100 rounded animate-pulse w-4/5 mx-auto" />
      </div>
    </div>
  );
}

function SuccessView({ alreadyConfirmed, navigate }) {
  return (
    <div
      className="flex flex-col items-center text-center py-6"
      data-testid="pionier-confirm-success"
    >
      <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
        {alreadyConfirmed
          ? "Schon bestätigt — willkommen zurück."
          : "Du bist auf der Vorab-Einblick-Liste."}
      </h1>
      <p className="mt-3 text-base text-slate-600 max-w-md">
        Wir melden uns, wenn es losgeht. Bis dahin bekommst du Vorab-Zugang
        zu deinem Flexpfad.
      </p>
      <Button
        className="mt-6 bg-[#4563BB] hover:bg-[#3a54a3] text-white"
        onClick={() => navigate("/warteliste/danke", { replace: true })}
        data-testid="pionier-confirm-continue-btn"
      >
        Weiter →
      </Button>
    </div>
  );
}

function ErrorView({ errorCode }) {
  const messageMap = {
    token_missing: {
      title: "Kein Bestätigungs-Link erkannt.",
      body: "Der Link scheint unvollständig zu sein. Bitte klicke den Link aus der Bestätigungs-Mail direkt an.",
    },
    token_required: {
      title: "Kein Token in der URL",
      body: "Bitte nutze den vollständigen Link aus deiner E-Mail.",
    },
    token_not_found: {
      title: "Bestätigung fehlgeschlagen.",
      body: "Der Link ist ungültig oder wurde manipuliert. Bitte nutze den Original-Link aus deiner E-Mail.",
    },
    token_expired: {
      title: "Dein Link ist abgelaufen.",
      body: "Bestätigungs-Links sind 7 Tage gültig. Bitte melde dich erneut auf der Warteliste an — wir schicken dir einen frischen Link.",
    },
    wrong_flow: {
      title: "Falscher Flow.",
      body: "Dieser Link gehört zu einem anderen Prozess. Bitte kontaktiere den Support.",
    },
    network_error: {
      title: "Verbindungsproblem.",
      body: "Wir konnten den Server nicht erreichen. Bitte prüfe deine Verbindung und versuche es erneut.",
    },
    internal_error: {
      title: "Technischer Fehler",
      body: "Da ist bei uns etwas schief gelaufen. Bitte versuche es in einem Moment noch einmal — oder schreib uns direkt.",
    },
    unknown: {
      title: "Bestätigung fehlgeschlagen.",
      body: "Bitte klicke den Link aus der E-Mail erneut. Wenn das nicht hilft, melde dich beim Support.",
    },
  };
  const msg = messageMap[errorCode] || messageMap.unknown;
  return (
    <div
      className="flex flex-col items-center text-center py-6"
      data-testid="pionier-confirm-error"
    >
      <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <AlertCircle className="w-8 h-8 text-red-600" />
      </div>
      <h1
        className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight"
        data-testid="pionier-confirm-error-title"
      >
        {msg.title}
      </h1>
      <p className="mt-3 text-base text-slate-600 max-w-md" data-testid="pionier-confirm-error-text">
        {msg.body}
      </p>
      <p className="mt-5 text-sm text-slate-500" data-testid="pionier-confirm-error-code">
        Fehler-Code: <span className="font-mono">{errorCode}</span>
      </p>
      <a
        href="mailto:hallo@flextalk.effektiv-kommunizieren.de?subject=Pionier-Confirm-Problem"
        className="mt-6 inline-flex items-center text-sm font-semibold text-[#4563BB] hover:underline"
        data-testid="pionier-confirm-support-link"
      >
        Support kontaktieren →
      </a>
    </div>
  );
}
